import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Database, Trash2, ArrowLeft, FileText, FileSpreadsheet, PenLine } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

const sourceIcon = { "Upload CSV": FileText, "Upload XLSX": FileSpreadsheet, "Manual Data": PenLine };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function DataSources() {
  const { activeOrgId, activeMembership } = useAuth();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const canDelete = activeMembership && ["Owner", "Administrator"].includes(activeMembership.role);

  const load = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/organizations/${activeOrgId}/imports`);
      setImports(data.imports);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try {
      await api.delete(`/organizations/${activeOrgId}/imports/${id}`);
      toast.success("Source supprimée");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, err.message));
    }
  };

  return (
    <div className="space-y-8" data-testid="data-sources-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link to="/data-hub" className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-[#1E3D2B]">
            <ArrowLeft size={14} /> Data Hub
          </Link>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#1E3D2B] sm:text-3xl">Data Sources</h1>
          <p className="mt-2 text-sm text-slate-500">Historique des imports de {activeMembership?.org.name}.</p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="data-sources-table">
        <SelectableTable rows={imports} rowId={(imp)=>imp.id} filename="datasources.csv" ariaLabel="Tableau — sélection et défilement">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-2 py-2"><SelectAllHeaderCheckbox /></th>
                <th className="px-5 py-3">Source</th>
                <th className="px-5 py-3">Type de données</th>
                <th className="px-5 py-3">Organisation</th>
                <th className="px-5 py-3">Période couverte</th>
                <th className="px-5 py-3">Date d'import</th>
                <th className="px-5 py-3">Lignes</th>
                {canDelete && <th className="px-5 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {imports.map((imp) => {
                const Icon = sourceIcon[imp.source] || Database;
                return (
                  <tr key={imp.id} className="border-b border-slate-100 last:border-0" data-testid={`import-row-${imp.id}`}><td className="px-2 py-2"><RowCheckbox id={imp.id} /></td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#ECF3EC] text-[#3E7256]"><Icon size={15} /></span>
                        <div>
                          <p className="text-sm font-semibold text-[#1E3D2B]">{imp.source_name}</p>
                          <p className="text-[11px] text-slate-400">{imp.source}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4"><span className="rounded-full bg-[#ECF3EC] px-2.5 py-1 text-xs font-semibold text-[#3E7256]">{imp.data_type}</span></td>
                    <td className="px-5 py-4 text-sm text-slate-600">{activeMembership?.org.name}</td>
                    <td className="px-5 py-4 font-mono text-xs text-slate-600">{imp.period_start} → {imp.period_end}</td>
                    <td className="px-5 py-4 text-sm text-slate-600">{fmtDate(imp.imported_at)}</td>
                    <td className="px-5 py-4 font-mono text-sm text-[#1E3D2B]">{imp.row_count}</td>
                    {canDelete && (
                      <td className="px-5 py-4">
                        <button onClick={() => remove(imp.id)} data-testid={`delete-import-${imp.id}`} className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-rose-600"><Trash2 size={15} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {!loading && imports.length === 0 && (
                <tr>
                  <td colSpan={canDelete ? 7 : 6} className="px-5 py-16 text-center">
                    <Database size={32} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm text-slate-400">Aucun import pour le moment.</p>
                    <Link to="/data-hub" className="mt-2 inline-block text-sm font-semibold text-[#3E7256] hover:underline">Importer des données →</Link>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </SelectableTable>
      </div>
    </div>
  );
}
